// Simplified Sales Import with date-based duplicate detection
// This is a simplified version to test and debug import issues

import React, { useState, useRef, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/hooks/use-toast';
import { useLanguage } from '@/contexts/LanguageContext';
import * as XLSX from 'xlsx';
import { triggerReloadSearchWithFile, pickImportFile, fileToBase64, base64ToFile } from '@/utils/reloadSearch';
import { ROUTES } from '@/lib/routes';
import type { CategoryKey } from '@/lib/navigation';

interface ImportPreviewData {
  [key: string]: any;
}

interface ProgressInfo {
  done: number;
  total: number;
}

type ImportStatus = 'idle' | 'uploading' | 'processing' | 'success' | 'error';

const ORDER_CATEGORY: CategoryKey = 'order';

const SalesImportSimplified: React.FC = () => {
  const navigate = useNavigate();
  const { currentCompany } = useAuth();
  const { toast } = useToast();
  const { language } = useLanguage();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [previewData, setPreviewData] = useState<ImportPreviewData[]>([]);
  const [importStatus, setImportStatus] = useState<ImportStatus>('idle');
  const [progress, setProgress] = useState<ProgressInfo>({ done: 0, total: 0 });

  useEffect(() => {
    // 检查是否有待恢复的文件状态
    const savedFileState = pickImportFile();
    if (savedFileState && savedFileState.importType === 'sales-simplified') {
      try {
        // 恢复文件
        const restoredFile = base64ToFile(
          savedFileState.fileContent,
          savedFileState.fileName,
          savedFileState.fileType
        );
        setSelectedFile(restoredFile);
        setPreviewData(savedFileState.previewData);
        setImportStatus('idle'); // 准备开始导入
        
        // 显示恢复提示
        toast.info('文件已恢复，正在开始导入...');
        
        // 短暂延迟后自动开始导入
        setTimeout(() => {
          handleImportInternal();
        }, 2000);
      } catch (error) {
        console.error('Failed to restore file:', error);
        toast.error('文件恢复失败');
      }
    }
  }, []);

  // Helper functions
  const toStringStrict = (v: any): string => {
    if (v === null || v === undefined) return '';
    return String(v).trim();
  };

  const toNumberOrNull = (v: any): number | null => {
    if (v === null || v === undefined || v === '') return null;
    const num = Number(v);
    return isNaN(num) ? null : num;
  };

  const toDateIsoOrNull = (dateValue: any, timeValue?: any): string | null => {
    if (!dateValue) return null;
    try {
      let dateStr = String(dateValue);
      if (timeValue) {
        dateStr += ' ' + String(timeValue);
      }
      const date = new Date(dateStr);
      if (isNaN(date.getTime())) return null;
      return date.toISOString();
    } catch {
      return null;
    }
  };

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setSelectedFile(file);
    setImportStatus('uploading');

    try {
      const buffer = await file.arrayBuffer();
      const workbook = XLSX.read(buffer, { type: 'buffer' });
      const worksheet = workbook.Sheets[workbook.SheetNames[0]];
      const data = XLSX.utils.sheet_to_json(worksheet, { header: 1 });
      
      // Simple header mapping - assume first row is headers
      const headers = data[0] as string[];
      const rows = data.slice(1) as any[][];
      
      const mappedData = rows.map(row => {
        const obj: any = {};
        headers.forEach((header, index) => {
          obj[header] = row[index];
        });
        return obj;
      });

      setPreviewData(mappedData.slice(0, 10)); // Show first 10 for preview
      setImportStatus('idle');
    } catch (error) {
      console.error('File parsing error:', error);
      toast.error('檔案解析失敗');
      setImportStatus('error');
    }
  };

  const handleImport = async () => {
    if (!currentCompany?.id || !selectedFile) {
      toast.error('請選擇公司和檔案');
      return;
    }

    // 保存文件状态并触发刷新
    try {
      const fileContent = await fileToBase64(selectedFile);
      const fileState = {
        fileName: selectedFile.name,
        fileSize: selectedFile.size,
        fileType: selectedFile.type,
        fileContent,
        previewData,
        importType: 'sales-simplified',
        timestamp: Date.now()
      };

      toast.info('正在准备导入...');
      
      triggerReloadSearchWithFile({ 
        importStarted: true, 
        importType: 'sales-simplified',
        timestamp: Date.now() 
      }, fileState);
      return;
    } catch (error) {
      console.error('Failed to save file state:', error);
      toast.error('保存文件状态失败');
    }
  };

  // 实际的导入逻辑
  const handleImportInternal = async () => {
    setImportStatus('processing');
    setProgress({ done: 0, total: previewData.length });

    try {
      const { supabase } = await import('@/integrations/supabase/client');
      const companyIdNum = Number(currentCompany.id);

      let failCount = 0;
      let successCount = 0;
      let firstErrMsg = '';

      // Re-parse full file for actual import
      const buffer = await selectedFile.arrayBuffer();
      const workbook = XLSX.read(buffer, { type: 'buffer' });
      const worksheet = workbook.Sheets[workbook.SheetNames[0]];
      const data = XLSX.utils.sheet_to_json(worksheet, { header: 1 });
      
      const headers = data[0] as string[];
      const rows = data.slice(1) as any[][];
      
      const fullData = rows.map(row => {
        const obj: any = {};
        headers.forEach((header, index) => {
          obj[header] = row[index];
        });
        return obj;
      });

      console.log(`Starting simplified import for ${fullData.length} records...`);

      for (let i = 0; i < fullData.length; i++) {
        const row = fullData[i];
        
        try {
          // Basic data extraction
          const orderId = toStringStrict(row['Order ID'] || row.order_id);
          const lineNo = Number(row['Line No'] || row.line_no || 1) || 1;
          const skuId = toStringStrict(row['SKU ID'] || row.sku_id);
          const quantity = toNumberOrNull(row['Qty (Q)'] || row.quantity || row.sale_quantity) ?? 0;
          const unitPrice = toNumberOrNull(row['Unit Price (U)'] || row.unit_price || row.sale_unit_price) ?? 0;
          const totalAmount = toNumberOrNull(row['Total Q * U - D = T'] || row.total_amount);
          const shipmentId = toStringStrict(row['Shipment ID'] || row.shipment_id);
          const orderDate = row['Order Date'] || row.order_date;
          
          if (!orderId || !skuId) {
            console.warn(`Row ${i + 1}: Missing required fields (order_id or sku_id)`);
            continue;
          }

          // Convert order_date to simple date string for comparison
          let orderDateStr: string | null = null;
          if (orderDate) {
            try {
              const date = new Date(orderDate);
              if (!isNaN(date.getTime())) {
                orderDateStr = date.toISOString().split('T')[0]; // YYYY-MM-DD
              }
            } catch (e) {
              console.warn(`Row ${i + 1}: Invalid order_date:`, orderDate);
            }
          }

          // Create basic payload
          const payload = {
            company_id: companyIdNum,
            order_id: orderId,
            line_no: lineNo,
            sku_id: skuId,
            sale_quantity: quantity,
            sale_unit_price: unitPrice,
            total_amount: totalAmount,
            shipment_id: shipmentId || null,
            order_date: orderDateStr,
            channel: 'HKTVmall'
          };

          // Check for duplicates ONLY within the same date
          let existingRecord = null;
          if (shipmentId && orderDateStr) {
            const { data: existing, error: queryErr } = await supabase
              .from('sales_orders')
              .select('id')
              .eq('company_id', companyIdNum)
              .eq('shipment_id', shipmentId)
              .eq('order_date', orderDateStr) // KEY OPTIMIZATION: Only search same date
              .maybeSingle();
            
            if (queryErr && queryErr.code !== 'PGRST116') {
              throw queryErr;
            }
            
            existingRecord = existing;
          }

          // Insert or update
          if (existingRecord) {
            // Update existing record
            const { error: updateErr } = await supabase
              .from('sales_orders')
              .update(payload)
              .eq('id', existingRecord.id);
              
            if (updateErr) {
              throw updateErr;
            }
            
            console.log(`Row ${i + 1}: Updated existing record for shipment_id: ${shipmentId}`);
          } else {
            // Insert new record
            const { error: insertErr } = await supabase
              .from('sales_orders')
              .insert([payload]);
              
            if (insertErr) {
              throw insertErr;
            }
            
            console.log(`Row ${i + 1}: Inserted new record for shipment_id: ${shipmentId}`);
          }
          
          successCount++;
          
        } catch (e: any) {
          failCount++;
          const errorMsg = `行 ${i + 1}: ${e?.message || e}`;
          console.error(`Import error for row ${i + 1}:`, e);
          if (!firstErrMsg) firstErrMsg = errorMsg;
        }

        setProgress({ done: i + 1, total: fullData.length });
        
        // Allow UI to update every 5 records
        if ((i + 1) % 5 === 0) {
          await new Promise(resolve => setTimeout(resolve, 10));
        }
      }

      // Show results
      if (failCount === 0) {
        toast.success(`匯入完成：${successCount} 筆記錄`);
      } else {
        toast.warning(`部分資料未成功寫入：${failCount} 筆。成功：${successCount} 筆。第一個錯誤：${firstErrMsg}`);
      }
      
      setImportStatus('success');
      
    } catch (e: any) {
      console.error('Import process error:', e);
      toast.error(`匯入失敗：${e.message}`);
      setImportStatus('error');
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold tracking-tight">銷售數據匯入 (簡化版)</h1>
        <Button
          variant="outline"
          onClick={() => {
            localStorage.setItem('selectedCategory', ORDER_CATEGORY);
            navigate(ROUTES.dashboard.root);
          }}
        >
          返回儀表板
        </Button>
      </div>

      <div className="grid gap-6">
        <div className="space-y-4">
          <h2 className="text-xl font-semibold">選擇檔案</h2>
          <input
            ref={fileInputRef}
            type="file"
            accept=".xlsx,.xls,.csv"
            onChange={handleFileChange}
            className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
          />
        </div>

        {previewData.length > 0 && (
          <div className="space-y-4">
            <h2 className="text-xl font-semibold">資料預覽 (前10筆)</h2>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    {Object.keys(previewData[0]).slice(0, 5).map((key) => (
                      <th key={key} className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        {key}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {previewData.map((row, index) => (
                    <tr key={index}>
                      {Object.values(row).slice(0, 5).map((value, cellIndex) => (
                        <td key={cellIndex} className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {String(value)}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="flex gap-4">
              <Button 
                onClick={handleImport}
                disabled={importStatus === 'processing'}
                className="bg-green-600 hover:bg-green-700"
              >
                {importStatus === 'processing' ? '匯入中...' : '確認匯入'}
              </Button>
            </div>

            {importStatus === 'processing' && (
              <div className="space-y-2">
                <div className="text-sm text-gray-600">
                  進度: {progress.done}/{progress.total}
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div 
                    className="bg-blue-600 h-2 rounded-full transition-all duration-300" 
                    style={{ width: `${(progress.done / progress.total) * 100}%` }}
                  />
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default SalesImportSimplified;
